import * as React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * ExpandableRow component for progressive disclosure in tables
 * Shows a summary row that can be expanded to reveal more details
 * 
 * @param {React.ReactNode} children - The main row content (always visible)
 * @param {React.ReactNode} expandedContent - Content shown when row is expanded
 * @param {boolean} defaultExpanded - Whether the row starts expanded (default: false)
 * @param {string} className - Additional CSS classes for the row
 * @param {function} onToggle - Callback when row is toggled
 */
export const ExpandableRow = ({
  children,
  expandedContent,
  defaultExpanded = false,
  className = '',
  onToggle,
  ...props
}) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

  const handleToggle = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    onToggle?.(newState);
  };

  return (
    <>
      <tr 
        className={cn(
          'cursor-pointer transition-colors',
          isExpanded ? 'bg-blue-50/30 dark:bg-blue-900/10' : '',
          className
        )}
        onClick={handleToggle}
        {...props}
      >
        {children}
        <td className="p-4 w-10">
          <button
            type="button"
            className="h-6 w-6 rounded flex items-center justify-center text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Inklappen' : 'Uitklappen'}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" strokeWidth={1.5} />
            ) : (
              <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
            )}
          </button>
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-neutral-50/50 dark:bg-neutral-800/30">
          <td colSpan="100%" className="p-0">
            <div className="px-6 py-4 border-t border-neutral-100 dark:border-neutral-800">
              {expandedContent}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

/**
 * ExpandableSection component for collapsible content sections
 * 
 * @param {string} title - Section title
 * @param {React.ReactNode} children - Section content
 * @param {boolean} defaultOpen - Whether section starts open
 * @param {string} className - Additional CSS classes
 */
export const ExpandableSection = ({
  title,
  children,
  defaultOpen = false,
  className = '',
  icon: Icon,
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  return (
    <div className={cn('border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-neutral-500" strokeWidth={1.5} />}
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
        ) : (
          <ChevronRight className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-white dark:bg-neutral-900">
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * DetailPanel component for showing expanded details in a structured way
 */
export const DetailPanel = ({ items, className = '' }) => {
  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4', className)}>
      {items.map((item, index) => (
        <div key={index} className="space-y-1">
          <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
            {item.label}
          </p>
          <p className="text-sm text-neutral-900 dark:text-white">
            {item.value || '-'}
          </p>
        </div>
      ))}
    </div>
  );
};

export default ExpandableRow;
